import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Import converters
import DocxConverter from './converters/DocxConverter.js';
import PdfConverter from './converters/PdfConverter.js';
import ComicConverter from './converters/ComicConverter.js';

class ConversionService {
  constructor() {
    // Initialize converters
    this.docxConverter = new DocxConverter();
    this.pdfConverter = new PdfConverter();
    this.comicConverter = new ComicConverter();
  }

  async convertFiles(files, options, onProgress) {
    const zip = new JSZip();
    let processedFiles = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const epubFileName = this.getEpubFileName(file.name);
      console.log(`Processing file: ${file.name} -> ${epubFileName}`);
      
      try {
        // Report starting conversion of current file
        onProgress({
          overallProgress: (processedFiles / files.length) * 100,
          currentFile: file.name,
          currentFileIndex: i + 1,
          totalFiles: files.length,
          status: 'converting'
        });

        const epubData = await this.convertToEpub(file, options, (fileProgress) => {
          // Report progress within current file
          const overallProgress = ((processedFiles + fileProgress / 100) / files.length) * 100;
          onProgress({
            overallProgress,
            currentFile: file.name,
            currentFileIndex: i + 1,
            totalFiles: files.length,
            fileProgress,
            status: 'converting'
          });
        });
        
        console.log(`Adding to ZIP: ${epubFileName}, data type:`, typeof epubData, 'size:', epubData.size || epubData.length || 'unknown');
        zip.file(epubFileName, epubData);
        processedFiles++;
        
        // Report completion of current file
        onProgress({
          overallProgress: (processedFiles / files.length) * 100,
          currentFile: file.name,
          currentFileIndex: i + 1,
          totalFiles: files.length,
          fileProgress: 100,
          status: 'completed'
        });
      } catch (error) {
        console.error(`Error converting ${file.name}:`, error);
        throw new Error(`Failed to convert ${file.name}: ${error.message}`);
      }
    }

    // Report starting ZIP generation
    onProgress({
      overallProgress: 95,
      currentFile: 'Generating ZIP file...',
      currentFileIndex: files.length,
      totalFiles: files.length,
      status: 'zipping'
    });

    // Generate and download the ZIP file
    const zipBlob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: options.compressionLevel }
    });
    
    saveAs(zipBlob, 'converted_epub_files.zip');
    
    // Report completion
    onProgress({
      overallProgress: 100,
      currentFile: 'All files converted!',
      currentFileIndex: files.length,
      totalFiles: files.length,
      status: 'finished'
    });
  }

  async convertToEpub(file, options, onFileProgress) {
    console.log('Converting file:', file.name);
    
    // Determine file type and use appropriate converter
    if (this.docxConverter.isDocxFile(file.name)) {
      return await this.docxConverter.convertToEpub(file, options, onFileProgress);
    } else if (this.pdfConverter.isPdfFile(file.name)) {
      console.log('PDF file detected, using PDF converter');
      try {
        const result = await this.pdfConverter.convertToEpub(file, options, onFileProgress);
        console.log('PDF conversion completed successfully, result type:', typeof result, 'size:', result.size);
        return result;
      } catch (pdfError) {
        console.error('PDF conversion failed:', pdfError);
        throw new Error(`PDF conversion failed: ${pdfError.message}`);
      }
    } else if (this.comicConverter.isComicFile(file.name)) {
      return await this.comicConverter.convertToEpub(file, options, onFileProgress);
    } else {
      // Default to comic converter for CBZ/ZIP files
      console.log('Treating as CBZ/ZIP file');
      return await this.comicConverter.convertToEpub(file, options, onFileProgress);
    }
  }

  getEpubFileName(originalFileName) {
    // Handle all supported file types
    return originalFileName
      .replace('.cbz', '.epub')
      .replace('.cb7', '.epub')
      .replace('.zip', '.epub')
      .replace('.docx', '.epub')
      .replace('.pdf', '.epub');
  }
}

export default ConversionService;